/**
 * The toolbox and the inspector for free elements.
 *
 * Toolbox (above the stage): drop text, a metric, a chart (any of the 30 styles), an image, a shape.
 * Inspector (right panel, when an element is selected): position, size, order, rotation, opacity,
 * fill / border / shadow / radius, and per kind — font, size, weight, alignment, colour; chart style,
 * labels and values; image fit; shape kind. Everything writes to the page's `elements` (or the legacy
 * `boxes` entry it came from), so the PDF and PPTX follow.
 */
import { useState } from 'react';
import { NumberField } from '@/cowork/shared/NumberField';
import { Type, BarChart3, Image as ImageIcon, Square, Hash, ChevronUp, ChevronDown, Copy, Trash2, AlignLeft, AlignCenter, AlignRight, Bold, Italic } from 'lucide-react';
import { CHART_CATALOG } from '@/cowork/omniad/components/slides/chartCatalog';
import { ChartDataEditor } from './ChartDataEditor';
import { chartSvg } from '@/cowork/omniad/components/slides/chartSvg';
import type { Theme } from '@/cowork/omniad/components/slides/SlideRenderer';
import { FONT_CHOICES, makeElement, emptiestSpot, type FreeElement, type ElementKind } from '@/cowork/omniad/components/slides/elements';

type Slide = Record<string, any>;
const btn = 'inline-flex items-center gap-1 rounded-md border border-[#d0d5dd] px-2 py-1 text-[11px] font-medium text-[#344054] hover:bg-[#f9fafb] disabled:opacity-50';
const lbl = 'mb-1 block text-[10px] font-semibold uppercase tracking-wide text-[#667085]';
const inp = 'w-full rounded-md border border-[#d0d5dd] px-2 py-1 text-[12px] text-[#101828] focus:border-[#be43ff] focus:outline-none';

/** Where the selected element lives and how to write it back. */
function locate(slide: Slide, id: string | null): { listKey: 'elements' | 'boxes'; i: number; el: FreeElement } | null {
  if (!id) return null;
  const els: any[] = Array.isArray(slide.elements) ? slide.elements : [];
  let i = els.findIndex((e) => e?.id === id);
  if (i >= 0) return { listKey: 'elements', i, el: els[i] };
  const boxes: any[] = Array.isArray(slide.boxes) ? slide.boxes : [];
  i = boxes.findIndex((b, j) => (b?.id || `legacy-${j}`) === id);
  if (i >= 0) { const b = boxes[i]; return { listKey: 'boxes', i, el: { id, kind: 'text', x: b.x, y: b.y, w: b.w, h: b.h, text: b.text || '', size: b.size, color: b.color, bold: b.bold, align: b.align, background: b.background } as FreeElement }; }
  return null;
}

export function ElementToolbox({ slide, update, theme, onSelect, stageRef }: { slide: Slide; update: (mutate: (s: Slide) => Slide) => void; theme: Theme; onSelect: (id: string) => void; stageRef?: React.RefObject<HTMLDivElement | null> }) {
  const [chartsOpen, setChartsOpen] = useState(false);
  const add = (kind: ElementKind, extra: Partial<FreeElement> = {}) => {
    const el = makeElement(kind, extra);
    // On a page with content, land in the emptiest spot instead of over what is there.
    if (slide.type !== 'free') { const spot = emptiestSpot(stageRef?.current || null, el.w, el.h); if (spot) { el.x = spot.x; el.y = spot.y; } }
    update((s) => ({ ...s, elements: [...(Array.isArray(s.elements) ? s.elements : []), el] }));
    onSelect(el.id);
    setChartsOpen(false);
  };
  const colors = { primary: theme.primary, accent: theme.accent, text: theme.text, textMuted: theme.textMuted, grid: theme.border };
  return (
    <div className="relative flex flex-wrap items-center gap-1">
      <button onClick={() => add('text')} className={btn} title="Text box"><Type className="h-3.5 w-3.5" /> Text</button>
      <button onClick={() => add('metric', { color: theme.primary })} className={btn} title="A big number with a label"><Hash className="h-3.5 w-3.5" /> Number</button>
      <button onClick={() => setChartsOpen((o) => !o)} className={`${btn} ${chartsOpen ? 'bg-[#f2f4f7]' : ''}`} title="A chart, any of the 30 styles"><BarChart3 className="h-3.5 w-3.5" /> Chart</button>
      <button onClick={() => add('image')} className={btn} title="An image or video poster"><ImageIcon className="h-3.5 w-3.5" /> Image</button>
      <button onClick={() => add('shape', { fill: theme.primary, opacity: 0.12 })} className={btn} title="A shape (rectangle, pill, circle, line)"><Square className="h-3.5 w-3.5" /> Shape</button>
      {chartsOpen && (
        <div className="absolute left-0 top-8 z-30 grid w-[560px] grid-cols-5 gap-1.5 rounded-xl border border-[#eaecf0] bg-white p-2 shadow-2xl">
          {CHART_CATALOG.map((st) => (
            <button key={st.id} onClick={() => add('chart', { chart: { type: st.id, ...st.sample }, title: st.label })} className="rounded-lg border border-transparent p-1 text-left hover:border-[#be43ff] hover:bg-[#fcf7ff]" title={st.when}>
              <div className="aspect-[16/9] w-full overflow-hidden rounded bg-[#f9fafb]" dangerouslySetInnerHTML={{ __html: chartSvg({ type: st.id, ...st.sample }, colors).replace('<svg ', '<svg style="width:100%;height:100%" ') }} />
              <div className="mt-0.5 truncate text-[10px] font-medium text-[#344054]">{st.label}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function ElementInspector({ slide, selectedId, update, onSelect, theme }: { slide: Slide; selectedId: string | null; update: (mutate: (s: Slide) => Slide) => void; onSelect: (id: string | null) => void; theme: Theme }) {
  const hit = locate(slide, selectedId);
  if (!hit) return null;
  const { listKey, i, el } = hit;
  const patch = (fields: Record<string, unknown>) => update((s) => { const list = [...(Array.isArray(s[listKey]) ? s[listKey] : [])]; list[i] = { ...list[i], ...fields }; return { ...s, [listKey]: list }; });
  const remove = () => { update((s) => ({ ...s, [listKey]: (Array.isArray(s[listKey]) ? s[listKey] : []).filter((_: unknown, j: number) => j !== i) })); onSelect(null); };
  const duplicate = () => { const copy = { ...el, id: Math.random().toString(36).slice(2, 10), x: Math.min(90, el.x + 3), y: Math.min(90, el.y + 3) }; update((s) => ({ ...s, elements: [...(Array.isArray(s.elements) ? s.elements : []), copy] })); onSelect(copy.id); };
  const order = (dir: 1 | -1) => patch({ z: Math.max(0, (el.z ?? 1) + dir) });
  const chartEl = el.kind === 'chart' ? el : null;
  const setChart = (fields: Record<string, unknown>) => chartEl && patch({ chart: { ...chartEl.chart, ...fields } });

  return (
    <div className="space-y-3 border-b border-[#eaecf0] p-4">
      <div className="flex items-center justify-between">
        <div className="text-[12px] font-semibold text-[#101828]">{el.kind === 'text' ? 'Text' : el.kind === 'metric' ? 'Number' : el.kind === 'chart' ? 'Chart' : el.kind === 'image' ? 'Image' : 'Shape'} element</div>
        <div className="flex items-center gap-1">
          <button onClick={() => order(1)} className={btn} title="Bring forward"><ChevronUp className="h-3 w-3" /></button>
          <button onClick={() => order(-1)} className={btn} title="Send backward"><ChevronDown className="h-3 w-3" /></button>
          <button onClick={duplicate} className={btn} title="Duplicate"><Copy className="h-3 w-3" /></button>
          <button onClick={remove} className={`${btn} text-[#b42318]`} title="Delete"><Trash2 className="h-3 w-3" /></button>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-1.5">
        {(['x', 'y', 'w', 'h'] as const).map((k) => (
          <div key={k}><label className={lbl}>{k.toUpperCase()} %</label><NumberField value={Number(el[k])} step={0.5} decimals={1} min={-50} max={150} onChange={(n) => { if (n != null) patch({ [k]: n }); }} className={inp} /></div>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        <div><label className={lbl}>Rotate °</label><NumberField value={el.rotate ?? 0} step={1} min={-360} max={360} onChange={(n) => patch({ rotate: n || undefined })} className={inp} /></div>
        <div><label className={lbl}>Opacity</label><NumberField value={el.opacity ?? 1} min={0} max={1} step={0.05} onChange={(n) => patch({ opacity: n ?? 1 })} className={inp} /></div>
        <div><label className={lbl}>Radius</label><NumberField value={el.radius ?? 0} min={0} max={20} step={0.2} onChange={(n) => patch({ radius: n || undefined })} className={inp} /></div>
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        <ColorField label="Fill" value={el.background} onChange={(v) => patch({ background: v })} />
        <ColorField label="Border" value={el.border?.color} onChange={(v) => patch({ border: v ? { color: v, width: el.border?.width ?? 0.15 } : undefined })} />
        <label className="flex items-end gap-1.5 pb-1 text-[11px] text-[#344054]"><input type="checkbox" checked={!!el.shadow} onChange={(e) => patch({ shadow: e.target.checked || undefined })} /> Shadow</label>
      </div>

      {(el.kind === 'text' || el.kind === 'metric') && (
        <>
          <div className="grid grid-cols-2 gap-1.5">
            <div><label className={lbl}>Font</label>
              <select value={el.font || ''} onChange={(e) => patch({ font: e.target.value || undefined })} className={inp}>
                <option value="">Brand font</option>
                {FONT_CHOICES.map((f) => <option key={f.family} value={f.family}>{f.label}</option>)}
              </select>
            </div>
            <div><label className={lbl}>Size (cqw)</label><NumberField value={el.size ?? (el.kind === 'metric' ? 5 : 1.6)} min={0.6} max={14} step={0.2} onChange={(n) => { if (n != null) patch({ size: n }); }} className={inp} /></div>
          </div>
          <div className="flex flex-wrap items-center gap-1">
            {(['left', 'center', 'right'] as const).map((a) => <button key={a} onClick={() => patch({ align: a })} className={`${btn} ${(el.align || 'left') === a ? 'bg-[#f2f4f7]' : ''}`}>{a === 'left' ? <AlignLeft className="h-3 w-3" /> : a === 'center' ? <AlignCenter className="h-3 w-3" /> : <AlignRight className="h-3 w-3" />}</button>)}
            {el.kind === 'text' && <>
              <button onClick={() => patch({ bold: !el.bold })} className={`${btn} ${el.bold ? 'bg-[#f2f4f7]' : ''}`}><Bold className="h-3 w-3" /></button>
              <button onClick={() => patch({ italic: !el.italic })} className={`${btn} ${el.italic ? 'bg-[#f2f4f7]' : ''}`}><Italic className="h-3 w-3" /></button>
              <button onClick={() => patch({ uppercase: !el.uppercase })} className={`${btn} ${el.uppercase ? 'bg-[#f2f4f7]' : ''}`}>AA</button>
              <select value={el.valign || 'top'} onChange={(e) => patch({ valign: e.target.value })} className="rounded-md border border-[#d0d5dd] px-1.5 py-1 text-[11px]"><option value="top">Top</option><option value="middle">Middle</option><option value="bottom">Bottom</option></select>
            </>}
            <ColorField label="Colour" value={el.color} onChange={(v) => patch({ color: v })} inline />
          </div>
          {el.kind === 'text' && (
            <div className="grid grid-cols-2 gap-1.5">
              <div><label className={lbl}>Line height</label><NumberField value={el.lineHeight ?? 1.3} min={0.8} max={2.5} step={0.05} onChange={(n) => { if (n != null) patch({ lineHeight: n }); }} className={inp} /></div>
              <div><label className={lbl}>Letter spacing (em)</label><NumberField value={el.letterSpacing ?? 0} min={-0.1} max={0.5} step={0.01} onChange={(n) => patch({ letterSpacing: n || undefined })} className={inp} /></div>
            </div>
          )}
        </>
      )}

      {chartEl && <ChartDataEditor chart={chartEl.chart} setChart={setChart} theme={theme} />}

      {el.kind === 'image' && (
        <div><label className={lbl}>Fit</label>
          <select value={el.media?.fit || 'cover'} onChange={(e) => patch({ media: { ...(el.media || {}), fit: e.target.value } })} className={inp}><option value="cover">Fill the box</option><option value="contain">Fit inside</option></select>
        </div>
      )}

      {el.kind === 'shape' && (
        <div className="grid grid-cols-3 gap-1.5">
          <div><label className={lbl}>Shape</label>
            <select value={el.shape} onChange={(e) => patch({ shape: e.target.value })} className={inp}><option value="rect">Rectangle</option><option value="pill">Pill</option><option value="circle">Circle</option><option value="line">Line</option></select>
          </div>
          <ColorField label="Fill" value={el.fill} onChange={(v) => patch({ fill: v })} />
          <ColorField label="Stroke" value={el.stroke} onChange={(v) => patch({ stroke: v })} />
        </div>
      )}
      <div className="text-[10px] text-[#98a2b3]">Arrow keys nudge · shift+arrows 5% · ⌫ deletes · ⌘D duplicates</div>
    </div>
  );
}

/** The chart page's data and style, in the right panel (the stage stays exactly as it presents). */
export function ChartPageEditor({ slide, update, theme }: { slide: Slide; update: (mutate: (s: Slide) => Slide) => void; theme: Theme }) {
  const chart = (slide.chart || {}) as { type?: string; labels?: string[]; datasets?: Array<{ label?: string; values?: number[] }>; unit?: string; colors?: string[] };
  const setChart = (fields: Record<string, unknown>) => update((s) => ({ ...s, chart: { ...(s.chart || {}), ...fields } }));
  const panel = ['text-left', 'text-right', 'image-left', 'image-right'].includes(slide.layout) ? slide.layout : '';
  return (
    <div className="space-y-3 border-b border-[#eaecf0] p-4">
      <div className="text-[12px] font-semibold text-[#101828]">Chart</div>
      <ChartDataEditor chart={chart} setChart={setChart} theme={theme} />
      <div><label className={lbl}>Side panel</label>
        <select value={panel} onChange={(e) => update((s) => ({ ...s, layout: e.target.value || undefined }))} className={inp}>
          <option value="">None</option><option value="text-left">Text left</option><option value="text-right">Text right</option><option value="image-left">Image left</option><option value="image-right">Image right</option>
        </select>
      </div>
    </div>
  );
}

/** Two or more elements selected: align and distribute them. */
export function AlignBar({ slide, ids, update }: { slide: Slide; ids: string[]; update: (mutate: (s: Slide) => Slide) => void }) {
  const apply = (fn: (els: any[]) => Map<string, Partial<{ x: number; y: number }>>) => update((s) => {
    const list: any[] = Array.isArray(s.elements) ? s.elements : [];
    const sel = list.filter((e) => ids.includes(e?.id));
    if (sel.length < 2) return s;
    const moves = fn(sel);
    return { ...s, elements: list.map((e) => (moves.has(e?.id) ? { ...e, ...moves.get(e.id) } : e)) };
  });
  const num = (v: unknown) => Number(v) || 0;
  const align = (how: 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom') => apply((els) => {
    const m = new Map<string, Partial<{ x: number; y: number }>>();
    const L = Math.min(...els.map((e) => num(e.x))), R = Math.max(...els.map((e) => num(e.x) + num(e.w)));
    const T = Math.min(...els.map((e) => num(e.y))), B = Math.max(...els.map((e) => num(e.y) + num(e.h)));
    for (const e of els) {
      if (how === 'left') m.set(e.id, { x: L }); else if (how === 'right') m.set(e.id, { x: R - num(e.w) }); else if (how === 'center') m.set(e.id, { x: (L + R) / 2 - num(e.w) / 2 });
      else if (how === 'top') m.set(e.id, { y: T }); else if (how === 'bottom') m.set(e.id, { y: B - num(e.h) }); else m.set(e.id, { y: (T + B) / 2 - num(e.h) / 2 });
    }
    return m;
  });
  const distribute = (axis: 'x' | 'y') => apply((els) => {
    const m = new Map<string, Partial<{ x: number; y: number }>>();
    const size = axis === 'x' ? 'w' : 'h';
    const sorted = [...els].sort((a, b) => num(a[axis]) - num(b[axis]));
    const first = num(sorted[0][axis]), last = num(sorted[sorted.length - 1][axis]) + num(sorted[sorted.length - 1][size]);
    const total = sorted.reduce((a, e) => a + num(e[size]), 0);
    const gap = Math.max(0, (last - first - total) / (sorted.length - 1));
    let pos = first;
    for (const e of sorted) { m.set(e.id, { [axis]: Math.round(pos * 10) / 10 } as any); pos += num(e[size]) + gap; }
    return m;
  });
  const b = 'rounded-md border border-[#d0d5dd] px-2 py-1 text-[11px] font-medium text-[#344054] hover:bg-[#f9fafb]';
  return (
    <div className="space-y-2 border-b border-[#eaecf0] p-4">
      <div className="text-[12px] font-semibold text-[#101828]">{ids.length} elements selected</div>
      <div className="flex flex-wrap gap-1">
        <button className={b} onClick={() => align('left')}>Left</button><button className={b} onClick={() => align('center')}>Centre</button><button className={b} onClick={() => align('right')}>Right</button>
        <button className={b} onClick={() => align('top')}>Top</button><button className={b} onClick={() => align('middle')}>Middle</button><button className={b} onClick={() => align('bottom')}>Bottom</button>
      </div>
      <div className="flex flex-wrap gap-1">
        <button className={b} onClick={() => distribute('x')}>Distribute horizontally</button><button className={b} onClick={() => distribute('y')}>Distribute vertically</button>
      </div>
      <div className="text-[10px] text-[#98a2b3]">Shift-click adds to the selection · arrows nudge all</div>
    </div>
  );
}

function ColorField({ label, value, onChange, inline }: { label: string; value?: string; onChange: (v: string | undefined) => void; inline?: boolean }) {
  const hex = typeof value === 'string' && /^#[0-9a-fA-F]{6}$/.test(value) ? value : '#000000';
  return (
    <div className={inline ? 'flex items-center gap-1' : ''}>
      {!inline && <label className={lbl}>{label}</label>}
      <div className="flex items-center gap-1">
        <label className="relative h-6 w-6 shrink-0 overflow-hidden rounded border border-[#d0d5dd]" title={label} style={{ background: value || 'transparent' }}>
          <input type="color" value={hex} onChange={(e) => onChange(e.target.value)} className="absolute inset-0 cursor-pointer opacity-0" />
        </label>
        <button onClick={() => onChange(undefined)} className="text-[10px] text-[#98a2b3] hover:text-[#344054]" title="Clear">×</button>
      </div>
    </div>
  );
}
